import
  React, {
  useEffect,
  useRef,
  useState
} from 'react';

import
  styled
from 'styled-components';

import {
  withCommon
} from 'common/hocs';

import {
  Logger,
} from 'common';

import PaystackPop from '@paystack/inline-js';

import {
  OrderProvider
} from 'providers';

const Container = styled.div`
  padding: 50px 0;
`;

const EventPaymentFrameUI = props => {
  const [expiryTime, setExpiryTime] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const checkIntervalRef = useRef(null);
  const popupRef = useRef(null);
  const transactionIdRef = useRef(null);
  const paymentSuccessRef = useRef(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(props.common.location.search);
    const accessCode = searchParams.get('access_code');
    const expiryTimeParam = searchParams.get('expiry');
    const orderIdParam = searchParams.get('orderId');
    
    if (!accessCode) {
      Logger.error('EventPaymentFrame', 'init', 'No access code provided');
      props.common.showToast('Payment session could not be started');
      props.common.navigate(-1);
      return;
    }

    if (!expiryTimeParam) {
      Logger.error('EventPaymentFrame', 'init', 'No payment expiry time provided');
      props.common.navigate(-1);
      return;
    }

    if (orderIdParam) {
      setOrderId(orderIdParam);
    }

    setExpiryTime(parseInt(expiryTimeParam));

    const now = Date.now();
    const secondsRemaining = Math.max(0, Math.floor((parseInt(expiryTimeParam) - now) / 1000));

    // if we land on this page with less than 40 seconds remaining, we need to navigate to the expired page
    if (secondsRemaining <= 40) {
      Logger.error('EventPaymentFrame', 'init', 'Payment session expired');
      props.common.navigate(`/payment-result/expired?order_id=WT-${orderIdParam}`);
      return;
    }

    const popup = new PaystackPop();
    popupRef.current = popup;

    if (popup.backgroundIframe?.style) {
      popup.backgroundIframe.style.backgroundColor = 'transparent';
    }
    if (popup.checkoutIframe?.style) {
      popup.checkoutIframe.style.opacity = '0';
    }

    popup.resumeTransaction(accessCode, {
      onLoad: (transaction) => {
        transactionIdRef.current = transaction.id;

        if (popupRef.current.checkoutIframe?.style) {
          popupRef.current.checkoutIframe.style.opacity = '1';
        }

        props.common.hideLoading();
      },
      onSuccess: (transaction) => {
        paymentSuccessRef.current = true;
        const url = new URL(transaction.redirecturl);
        const queryParams = url.search;
        props.common.navigate(`/payment-result/success${queryParams}`, { replace: true });
      },
      onError: (error) => {
        Logger.error('EventPaymentFrame', 'paymentError', error);
        props.common.showToast('Oops! Something didn\'t go as planned. Please try again.');
        props.common.navigate(-1);
      },
      onCancel: () => {
        if (!isNaN(orderIdParam)) {
          OrderProvider.cancelOrder(parseInt(orderIdParam))
            .catch(e => Logger.error('EventPaymentFrame', 'cancelOrder', e));
        }
        
        if (transactionIdRef.current) {
          transactionIdRef.current = null;
          props.common.navigate(-1);
        }
      }
    });

    return () => {
      // Clean up interval on unmount
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }

      if (popupRef.current && transactionIdRef.current && !paymentSuccessRef.current) {
        const tr = transactionIdRef.current;
        transactionIdRef.current = null;
        popupRef.current.cancelTransaction(tr);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!expiryTime) return;

    const setupExpiryCheck = () => {
      checkIntervalRef.current = setInterval(() => {
        const now = Date.now();
        const secondsRemaining = Math.max(0, Math.floor((expiryTime - now) / 1000));
        
        if (secondsRemaining <= 0) {
          // Session expired
          if (checkIntervalRef.current) {
            clearInterval(checkIntervalRef.current);
          }
          Logger.error('EventPaymentFrame', 'checkExpiry', 'Payment session expired');
          props.common.navigate(`/payment-result/expired?order_id=WT-${orderId}`);
        }
      }, 1000); // Update every second
    };
    
    setupExpiryCheck();

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [expiryTime, orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Container>
    </Container>
  );
};

export const EventPaymentFrame = withCommon(EventPaymentFrameUI, {
  showHeader: false,
  showFooter: false,
  showLoadingOnLoad: true
});
